/* eslint-disable react/require-default-props */
import { forwardRef, useCallback, useEffect, useState } from 'react'

import IconButton from '@components/IconButton/IconButton'
import { faClose, faSearch } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { InputAdornment, SxProps, TextField, useTheme } from '@mui/material'
import { getTestId } from '@utils/helpers'

type Props = {
	id?: string
	value?: string
	placeholder?: string
	label?: string | number
	searchOnEnter?: boolean
	searchOnBlur?: boolean
	disabled?: boolean
	error?: boolean
	errorText?: string
	helperText?: string | null
	size?: 'small' | 'medium'
	autoFocus?: boolean
	sx?: SxProps
	onChange: (value: string) => void
}
type Ref = HTMLInputElement

/**
 * Stateless SearchInput Component which provides ability to 'clear' the input
 *
 * @example
 * <SearchInput searchOnEnter value={value} onChange={handleChange} />
 */
const SearchInput = forwardRef<Ref, Props>((props, ref) => {
	const testid = getTestId(props, 'search-input')

	const {
		id = null,
		value = '',
		label = '',
		placeholder = 'Search...',
		onChange,
		searchOnEnter = false,
		searchOnBlur = false,
		disabled = false,
		size = 'small',
		error = false,
		helperText = null,
		errorText = null,
		...rest
	} = props
	const [savedValue, setSavedValue] = useState<string>(value)
	const theme = useTheme()
	const deferred = searchOnEnter || searchOnBlur

	// Keep internal savedValue in sync when external value changes (e.g. URL/query updates)
	useEffect(() => {
		if (deferred || value === '') {
			setSavedValue(value)
		}
	}, [deferred, value])

	const handleChange = useCallback(
		(value) => {
			if (deferred) {
				setSavedValue(value)
				return
			}

			onChange(value)
		},
		[onChange, deferred]
	)

	const handleKeyUp = (event) => {
		if (searchOnEnter && (event.code === 'Enter' || event.code === 'NumpadEnter')) {
			onChange(savedValue)
		}
	}

	const handleBlur = () => {
		if (searchOnBlur && savedValue !== value) {
			onChange(savedValue)
		}
	}

	const handleClear = () => {
		onChange('')
		setSavedValue('')
	}

	return (
		<TextField
			ref={ref}
			fullWidth
			autoComplete="off"
			variant="outlined"
			id={id}
			value={deferred ? savedValue : value}
			label={label}
			placeholder={placeholder}
			onChange={(event) => handleChange(event.target.value)}
			onKeyUp={handleKeyUp}
			onBlur={handleBlur}
			disabled={disabled}
			error={error}
			helperText={error ? errorText : helperText}
			size={size}
			{...rest}
			slotProps={{
				input: {
					inputProps: {
						'data-testid': testid,
					},
					endAdornment: (
						<InputAdornment position="end">
							{value || savedValue ? (
								<IconButton
									size={size}
									disabled={disabled}
									sx={{ p: 0 }}
									onClick={handleClear}
									icon={faClose}
								/>
							) : null}
						</InputAdornment>
					),
					startAdornment: (
						<FontAwesomeIcon
							style={{ marginRight: '0.5rem' }}
							icon={faSearch}
							color={theme.palette.grey[600]}
							size="sm"
						/>
					),
				},
			}}
		/>
	)
})

export default SearchInput
